import { ColumnsType } from 'antd/es/table';
import React, { FC, memo, useMemo } from 'react';
import { useGetCurrencySymbol } from 'app/appState';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import {
  getSizeCodeTypeString,
  SizeCode,
  SizeCodeForListing,
  useGetPaginatedSizeCodesQuery,
  useGetSizeCodesByWarehouseIdQuery,
} from 'entities/SizeCode';
import { stepperModalActions } from 'features/StepperModal';
import { ServerTable } from 'shared/ui/ServerTable';
import { ServerTableRowSelectionActions } from 'shared/ui/ServerTable/types';
import { getSelectedWarehouse } from '../../model/selectors';
import { changePricesModalActions } from '../../model/slice/changePricesModalSlice';
import { orderBy } from 'lodash';

export const SelectSizesStep: FC = memo(() => {
  const { t } = useAppTranslation(['boxes', 'contracts', 'common']);

  const dispatch = useAppDispatch();

  const selectedWarehouse = useAppSelector(getSelectedWarehouse);
  const currencySymbol = useGetCurrencySymbol();
  const { data: warehouseSizeCodes } = useGetSizeCodesByWarehouseIdQuery(selectedWarehouse?.warehouseId, {
    skip: !selectedWarehouse?.warehouseId,
  });

  const columns = useMemo<ColumnsType<SizeCodeForListing>>(
    () => [
      {
        title: t('Square'),
        key: 'square',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, record) => (
          <div className="font-bold">
            {record.square} {getSizeCodeTypeString(record.sizeCodeType)}
          </div>
        ),
      },
      {
        title: t('Size group'),
        key: 'sizeGroup',
        render: (_, record) => <div className="text-primary font-normal">{record.sizeGroup}</div>,
      },
      {
        title: t('Cost'),
        key: 'monthRate',
        render: (_, record) => (
          <div className="text-primary">
            {record.dailyRate && (
              <div>
                {record.dailyRate} {currencySymbol}
                <span className="text-sm text-primaryLight">/{t('Day')}</span>
              </div>
            )}
            {record.weekRate && (
              <div>
                {record.weekRate} {currencySymbol}
                <span className="text-sm text-primaryLight">/{t('Week')}</span>
              </div>
            )}
            {record.monthRate && (
              <div>
                {record.monthRate} {currencySymbol}
                <span className="text-sm text-primaryLight">/{t('Month')}</span>
              </div>
            )}
          </div>
        ),
      },
    ],
    [currencySymbol, t],
  );

  const rowSelectionActions: Array<ServerTableRowSelectionActions<SizeCode>> = [
    {
      name: t('Next', { ns: 'common' }),
      onClick: (selectedElements) => {
        dispatch(changePricesModalActions.setSelectedSizeCodes(selectedElements));
        dispatch(stepperModalActions.changeActiveStepIndex(2));
      },
    },
  ];

  return (
    <ServerTable
      columns={columns}
      fetchData={useGetPaginatedSizeCodesQuery}
      rowKey="sizeCodeId"
      search
      title={t('Choose sizes')}
      selectionType="checkbox"
      rowSelectionActions={rowSelectionActions}
      defaultFilters={{ warehouseId: selectedWarehouse?.warehouseId, orderBy: 'square:ASC' }}
      selectAllRecords={warehouseSizeCodes}
    />
  );
});
